import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../components/ui/StatusBadge';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import ActionButton from '../components/ui/ActionButton';
import Modal from '../components/ui/Modal';
import DataTable, { Column } from '../components/ui/DataTable';

const AR_API = `${API_V1}/ar-invoices`;
const REV_API = `${API_V1}/revenue`;

interface Obligation { id: number; po_number: number; description: string; standalone_selling_price: number; allocated_price: number; amount_recognized: number; recognition_method: string; status: string; }
interface Contract { id: number; contract_number: string; customer_id: string; customer_name: string; total_value: number; status: string; start_date: string; end_date: string; obligations: Obligation[]; }
interface Summary { total_contracts: number; total_transaction_price: number; total_recognized: number; total_deferred: number; pct_recognized: number; }

interface SubscriptionRow {
  contract_id: number;
  contract_number: string;
  title: string | null;
  customer_name: string | null;
  entity_code: string | null;
  currency: string;
  lifecycle_status: string;
  effective_date: string | null;
  expiration_date: string | null;
  term_months: number;
  monthly_amount: number;
  billing_frequency: string;
  total_contract_value: number;
  auto_renew: boolean;
  cancellation_notice_days: number;
}

interface ChurnBlock {
  status: string;
  snapshots_available?: number;
  snapshots_required?: number;
  logo_churn_pct: number | null;
  revenue_churn_pct: number | null;
  lookback_days?: number;
}

interface RenewalCalendarEntry {
  contract_id: number;
  contract_number: string;
  title: string | null;
  customer_name: string | null;
  entity_code: string | null;
  monthly_amount: number;
  currency: string;
  end_date: string;
  days_to_renewal: number;
  decision_deadline: string;
  cancellation_notice_days: number;
  auto_renew: boolean;
  renewal_price_basis: string | null;
}

interface SubscriptionKpis {
  entity_code: string | null;
  as_of: string;
  mrr: number;
  arr: number;
  active_count: number;
  missing_metadata_count: number;
  churn: ChurnBlock;
  renewal_calendar: RenewalCalendarEntry[];
  renewal_calendar_days_ahead: number;
}

const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', padding: '10px 14px', color: 'var(--text-hint)', letterSpacing: '0.04em', borderBottom: '1px solid var(--border-subtle)' };
const tdStyle: React.CSSProperties = { fontSize: 13, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' };

// ── Phase 16 Track B — CECL assessment types + helpers ────────

interface CECLAllowanceHistoryRow {
  event_type: string;
  amount: number;
  direction: string;
  je_id: number | null;
  methodology: string | null;
  rationale_text: string | null;
  triggered_by: string | null;
  fired_at: string | null;
  computation_inputs?: any;
}

interface CECLAllowance {
  contract_id: number;
  assessment_basis: 'POOL' | 'INDIVIDUAL' | null;
  pool_id: number | null;
  pool_code: string | null;
  pool_name?: string | null;
  current_allowance_amount: number | null;
  current_exposure: number | null;
  last_computed_at: string | null;
  specific_reserve_amount?: number | null;
  rationale?: string | null;
  history: CECLAllowanceHistoryRow[];
  // Tolerate older shape with different key names
  cecl_assessment_basis?: 'POOL' | 'INDIVIDUAL' | null;
  cecl_pool_id?: number | null;
  current_allowance?: number | string | null;
}

interface CECLPoolOption {
  id: number;
  pool_code: string;
  pool_name: string;
  is_active: boolean;
}

type CECLModalKind = 'assign_pool' | 'set_individual' | null;
interface CECLModalState {
  contract_id: number;
  kind: CECLModalKind;
  pool_id: number | null;
  specific_reserve_amount: string;
  rationale: string;
}

const normalizeCECL = (raw: any, contract_id: number): CECLAllowance => ({
  contract_id,
  assessment_basis: raw?.assessment_basis ?? raw?.cecl_assessment_basis ?? null,
  pool_id: raw?.pool_id ?? raw?.cecl_pool_id ?? null,
  pool_code: raw?.pool_code ?? null,
  pool_name: raw?.pool_name ?? null,
  current_allowance_amount: raw?.current_allowance_amount != null
    ? Number(raw.current_allowance_amount)
    : (raw?.current_allowance != null ? Number(raw.current_allowance) : null),
  current_exposure: raw?.current_exposure != null ? Number(raw.current_exposure) : null,
  last_computed_at: raw?.last_computed_at ?? null,
  specific_reserve_amount: raw?.specific_reserve_amount != null ? Number(raw.specific_reserve_amount) : null,
  rationale: raw?.rationale ?? null,
  history: Array.isArray(raw?.history) ? raw.history : [],
});

export default function RevenueContracts() {
  const { entity } = useGlobalEntity();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);
  const [kpis, setKpis] = useState<SubscriptionKpis | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // ASC 606 Phase 9g — Step 3 summary per-contract (lazy-loaded on expand)
  const [step3Summary, setStep3Summary] = useState<Record<number, {
    vc_aggregate: number; vc_components: number; vc_pending: number;
    refund_count: number; refund_open: number; refund_outstanding: number;
    cps_count: number; cps_open: number; cps_outstanding: number;
    financing_adjustment: number; noncash_fv: number | null;
  }>>({});
  // Phase 16 Track B — CECL assessment per-contract.
  // Full assignment + individual-basis + compute + history actions.
  const [ceclBasis, setCeclBasis] = useState<Record<number, CECLAllowance>>({});
  const [ceclPoolList, setCeclPoolList] = useState<CECLPoolOption[]>([]);
  const [ceclModal, setCeclModal] = useState<CECLModalState | null>(null);
  const [ceclSubmitting, setCeclSubmitting] = useState(false);
  const [ceclError, setCeclError] = useState<string | null>(null);
  const [ceclHistoryOpenId, setCeclHistoryOpenId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<string>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, sRes, subRes, kpiRes] = await Promise.all([
        authFetch(withEntityParam(`${AR_API}/contracts?limit=50`, entity)),
        authFetch(withEntityParam(`${REV_API}/summary`, entity)),
        authFetch(withEntityParam(`${REV_API}/subscriptions`, entity)),
        authFetch(withEntityParam(`${REV_API}/subscriptions/kpis`, entity)),
      ]);
      if (cRes.ok) { const d = await cRes.json(); setContracts(d.items || []); }
      if (sRes.ok) setSummary(await sRes.json());
      if (subRes.ok) { const d = await subRes.json(); setSubscriptions(d.subscriptions || []); }
      if (kpiRes.ok) setKpis(await kpiRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  // Re-fetches automatically when the global entity selector changes
  useEffect(() => { load(); }, [load]);

  const allContractsTab = (
    <>
      {summary && (
        <KpiGrid columns={5} items={[
          { label: 'Contracts', value: summary.total_contracts },
          { label: 'Total Value', value: formatAmountCompact(summary.total_transaction_price), color: 'var(--accent)' },
          { label: 'Recognized', value: formatAmountCompact(summary.total_recognized), color: 'var(--positive)' },
          { label: 'Deferred', value: formatAmountCompact(summary.total_deferred), color: 'var(--warning)' },
          { label: 'Progress', value: `${summary.pct_recognized}%`, color: 'var(--accent)' },
        ]} />
      )}

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : contracts.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No revenue contracts.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {contracts.map(c => (
            <div key={c.id} style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
              <div onClick={() => {
                const next = expandedId === c.id ? null : c.id;
                setExpandedId(next);
                // Lazy-load Step 3 summary when expanding, once per contract
                if (next && !step3Summary[c.id]) {
                  (async () => {
                    try {
                      const [vcRes, rlRes, cpsRes] = await Promise.all([
                        authFetch(`${REV_API}/contracts/${c.id}/variable-consideration-result`),
                        authFetch(`${REV_API}/refund-liabilities?contract_id=${c.id}`),
                        authFetch(`${REV_API}/contracts/${c.id}/cps`),
                      ]);
                      const vc = vcRes.ok ? await vcRes.json() : null;
                      const rl = rlRes.ok ? await rlRes.json() : { liabilities: [] };
                      const cps = cpsRes.ok ? await cpsRes.json() : { payments: [] };
                      // Pull financing/noncash from review serializer if available
                      const rvRes = await authFetch(`${REV_API}/reviews?entity_code=${encodeURIComponent('')}&limit=500`);
                      let financing = 0; let noncashFv: number | null = null;
                      if (rvRes.ok) {
                        const rvData = await rvRes.json();
                        const rv = (rvData.reviews || []).find((r: any) => r.revenue_contract_id === c.id);
                        if (rv) {
                          financing = rv.financing_adjustment || 0;
                          noncashFv = rv.noncash_consideration_fv ?? null;
                        }
                      }
                      // Phase 16 Track B — CECL assessment fetch (non-blocking).
                      // Failures are swallowed so the rest of the Step 3 summary still renders.
                      (async () => {
                        try {
                          const ceclRes = await authFetch(
                            `${REV_API}/contracts/${c.id}/cecl/allowance`,
                          );
                          if (ceclRes.ok) {
                            const cecl = await ceclRes.json();
                            setCeclBasis(prev => ({
                              ...prev,
                              [c.id]: normalizeCECL(cecl, c.id),
                            }));
                          }
                        } catch { /* non-fatal — section falls back to "not set" */ }
                      })();
                      setStep3Summary(prev => ({
                        ...prev,
                        [c.id]: {
                          vc_aggregate: vc?.variable_consideration_aggregate || 0,
                          vc_components: (vc?.components || []).length,
                          vc_pending: (vc?.components || []).reduce(
                            (s: number, x: any) => s + (x.pending_adjustment_amount || 0), 0,
                          ),
                          refund_count: (rl.liabilities || []).length,
                          refund_open: (rl.liabilities || []).filter(
                            (r: any) => r.status === 'OPEN' || r.status === 'ACCRUED',
                          ).length,
                          refund_outstanding: (rl.liabilities || []).reduce(
                            (s: number, r: any) => s + (r.liability_balance || 0), 0,
                          ),
                          cps_count: (cps.payments || []).length,
                          cps_open: (cps.payments || []).filter(
                            (p: any) => p.status === 'OPEN' || p.status === 'ACCRUED',
                          ).length,
                          cps_outstanding: (cps.payments || []).reduce(
                            (s: number, p: any) => s + (p.balance_due || 0), 0,
                          ),
                          financing_adjustment: financing,
                          noncash_fv: noncashFv,
                        },
                      }));
                    } catch { /* non-fatal — table still renders without summary */ }
                  })();
                }
              }} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{c.contract_number}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.customer_name || c.customer_id} — {formatAmount(c.total_value)}</div>
                </div>
                <StatusBadge status={c.status} />
              </div>
              {expandedId === c.id && step3Summary[c.id] && (
                <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-deep)', fontSize: 11 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
                    Step 3 Summary (ASC 606-10-32)
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', color: 'var(--text-secondary)' }}>
                    <span>VC aggregate: <strong style={{
                      color: step3Summary[c.id].vc_aggregate === 0
                        ? 'var(--text-secondary)'
                        : step3Summary[c.id].vc_aggregate > 0 ? 'var(--positive)' : 'var(--negative)',
                    }}>{formatAmount(step3Summary[c.id].vc_aggregate)}</strong></span>
                    <span>{step3Summary[c.id].vc_components} component{step3Summary[c.id].vc_components === 1 ? '' : 's'}</span>
                    {step3Summary[c.id].vc_pending !== 0 && (
                      <span style={{ color: 'var(--warning)', fontWeight: 600 }}>
                        pending Δ {formatAmount(step3Summary[c.id].vc_pending)}
                      </span>
                    )}
                    {step3Summary[c.id].refund_count > 0 && (
                      <span>
                        refunds: {step3Summary[c.id].refund_open}/{step3Summary[c.id].refund_count} open
                        {step3Summary[c.id].refund_outstanding > 0 && (
                          <> · bal <strong style={{ color: 'var(--warning)' }}>
                            {formatAmount(step3Summary[c.id].refund_outstanding)}
                          </strong></>
                        )}
                      </span>
                    )}
                    {step3Summary[c.id].cps_count > 0 && (
                      <span>
                        CPS: {step3Summary[c.id].cps_open}/{step3Summary[c.id].cps_count} open
                        {step3Summary[c.id].cps_outstanding > 0 && (
                          <> · bal <strong style={{ color: 'var(--warning)' }}>
                            {formatAmount(step3Summary[c.id].cps_outstanding)}
                          </strong></>
                        )}
                      </span>
                    )}
                    {step3Summary[c.id].financing_adjustment !== 0 && (
                      <span>financing adj: <strong>{formatAmount(step3Summary[c.id].financing_adjustment)}</strong></span>
                    )}
                    {step3Summary[c.id].noncash_fv !== null && (
                      <span>noncash FV: <strong>{formatAmount(step3Summary[c.id].noncash_fv!)}</strong></span>
                    )}
                  </div>
                </div>
              )}
              {/* Phase 16 Track B — CECL Assessment section */}
              {expandedId === c.id && (
                <CECLAssessmentSection
                  contract={c}
                  allowance={ceclBasis[c.id] || null}
                  poolOptions={ceclPoolList}
                  historyOpen={ceclHistoryOpenId === c.id}
                  onToggleHistory={() =>
                    setCeclHistoryOpenId(prev => prev === c.id ? null : c.id)
                  }
                  onOpenAssignPool={async () => {
                    if (ceclPoolList.length === 0) {
                      try {
                        const r = await authFetch(`${REV_API}/cecl/pools?active_only=true`);
                        if (r.ok) {
                          const d = await r.json();
                          setCeclPoolList((d.pools || []).map((p: any) => ({
                            id: p.id, pool_code: p.pool_code, pool_name: p.pool_name,
                            is_active: p.is_active,
                          })));
                        }
                      } catch { /* modal opens with empty list */ }
                    }
                    setCeclError(null);
                    setCeclModal({
                      contract_id: c.id,
                      kind: 'assign_pool',
                      pool_id: null,
                      specific_reserve_amount: '',
                      rationale: '',
                    });
                  }}
                  onOpenSetIndividual={() => {
                    setCeclError(null);
                    setCeclModal({
                      contract_id: c.id,
                      kind: 'set_individual',
                      pool_id: null,
                      specific_reserve_amount: '',
                      rationale: '',
                    });
                  }}
                  onCompute={async () => {
                    setCeclError(null);
                    try {
                      const r = await authFetch(
                        `${REV_API}/contracts/${c.id}/cecl/compute-allowance`,
                        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) },
                      );
                      if (r.ok) {
                        const refresh = await authFetch(`${REV_API}/contracts/${c.id}/cecl/allowance`);
                        if (refresh.ok) {
                          const cecl = await refresh.json();
                          setCeclBasis(prev => ({ ...prev, [c.id]: normalizeCECL(cecl, c.id) }));
                        }
                      } else {
                        const err = await r.json().catch(() => ({}));
                        const detail = err?.detail || err?.message || `HTTP ${r.status}`;
                        const code = err?.error_code || err?.code;
                        if (code === 'CECLEngineDisabledError' || (typeof detail === 'string' && detail.includes('CECLEngineDisabled'))) {
                          setCeclError('Engine inert — enable via CECL Pools page first');
                        } else {
                          setCeclError(typeof detail === 'string' ? detail : JSON.stringify(detail));
                        }
                      }
                    } catch (e: any) {
                      setCeclError(e?.message || 'Network error');
                    }
                  }}
                  error={ceclError}
                />
              )}
              {expandedId === c.id && c.obligations && c.obligations.length > 0 && (
                <div style={{ padding: '0 16px 14px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {['#', 'Description', 'Method', 'SSP', 'Allocated', 'Recognized', 'Status'].map(h => (
                        <th key={h} style={{ ...thStyle, textAlign: (['SSP', 'Allocated', 'Recognized'] as string[]).includes(h) ? 'right' : 'left' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{c.obligations.map(o => (
                      <tr key={o.id}>
                        <td style={tdStyle}>{o.po_number}</td>
                        <td style={tdStyle}>{o.description}</td>
                        <td style={{ ...tdStyle, fontSize: 11 }}>{o.recognition_method}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(o.standalone_selling_price)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(o.allocated_price)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: o.amount_recognized > 0 ? 'var(--positive)' : 'var(--text-secondary)' }}>{formatAmount(o.amount_recognized)}</td>
                        <td style={tdStyle}><StatusBadge status={o.status} /></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  const churnCard = (() => {
    if (!kpis) return null;
    const c = kpis.churn;
    if (c.status === 'INSUFFICIENT_HISTORY') {
      const have = c.snapshots_available || 0;
      const need = c.snapshots_required || 30;
      return {
        label: 'Churn',
        value: 'N/A',
        subtitle: `${have}/${need} days of history`,
        color: 'var(--text-hint)',
      };
    }
    if (c.status === 'NO_STARTING_POPULATION') {
      return { label: 'Churn', value: 'N/A', subtitle: 'no starting pop', color: 'var(--text-hint)' };
    }
    if (c.status === 'ERROR') {
      return { label: 'Churn', value: 'Error', subtitle: '', color: 'var(--negative)' };
    }
    return null;
  })();

  const subscriptionsTab = (
    <>
      {kpis ? (
        <KpiGrid columns={5} items={[
          { label: 'MRR', value: formatAmountCompact(kpis.mrr), color: 'var(--positive)' },
          { label: 'ARR', value: formatAmountCompact(kpis.arr), color: 'var(--accent)' },
          { label: 'Active Subscriptions', value: kpis.active_count },
          churnCard || {
            label: 'Logo Churn',
            value: kpis.churn.logo_churn_pct != null ? `${kpis.churn.logo_churn_pct.toFixed(2)}%` : 'N/A',
            subtitle: kpis.churn.lookback_days ? `${kpis.churn.lookback_days}d` : '',
            color: (kpis.churn.logo_churn_pct ?? 0) > 5 ? 'var(--negative)' : 'var(--positive)',
          },
          churnCard || {
            label: 'Revenue Churn',
            value: kpis.churn.revenue_churn_pct != null ? `${kpis.churn.revenue_churn_pct.toFixed(2)}%` : 'N/A',
            subtitle: kpis.churn.lookback_days ? `${kpis.churn.lookback_days}d` : '',
            color: (kpis.churn.revenue_churn_pct ?? 0) > 5 ? 'var(--negative)' : 'var(--positive)',
          },
        ]} />
      ) : null}

      {kpis && kpis.churn.status === 'INSUFFICIENT_HISTORY' && (
        <div style={{ padding: 12, background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.18)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Churn metrics require at least {kpis.churn.snapshots_required || 30} days of MRR snapshots before they become meaningful.
          Currently {kpis.churn.snapshots_available || 0} day(s) of history are available. The daily snapshot job runs automatically
          in the heartbeat — check back tomorrow to see progress. Reporting 0% before then would be misleading.
        </div>
      )}

      {kpis && kpis.missing_metadata_count > 0 && (
        <div style={{ padding: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
          {kpis.missing_metadata_count} active subscription(s) are missing monthly_amount metadata and contribute $0 to MRR. Re-extract
          or manually edit those contracts to restore accurate MRR.
        </div>
      )}

      <h2 style={{ fontSize: 13, fontWeight: 700, marginTop: 16, marginBottom: 8, color: 'var(--navy-900)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Active Subscriptions ({subscriptions.length})
      </h2>
      {subscriptions.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>
          No subscription contracts yet. Upload a SUBSCRIPTION contract via Contract Intelligence to see it here.
        </div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thStyle}>Contract #</th>
              <th style={thStyle}>Customer</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Monthly</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>TCV</th>
              <th style={thStyle}>Billing</th>
              <th style={thStyle}>Term</th>
              <th style={thStyle}>Ends</th>
              <th style={thStyle}>Status</th>
            </tr></thead>
            <tbody>
              {subscriptions.map(s => (
                <tr key={s.contract_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/contracts/${s.contract_id}`)}>
                  <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--accent)' }}>{s.contract_number}</td>
                  <td style={tdStyle}>{s.customer_name || '-'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(s.monthly_amount)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatAmount(s.total_contract_value)}</td>
                  <td style={{ ...tdStyle, fontSize: 11 }}>{s.billing_frequency}</td>
                  <td style={tdStyle}>{s.term_months} mo</td>
                  <td style={tdStyle}>{s.expiration_date || '-'}</td>
                  <td style={tdStyle}><StatusBadge status={s.lifecycle_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 style={{ fontSize: 13, fontWeight: 700, marginTop: 24, marginBottom: 8, color: 'var(--navy-900)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Renewal Calendar — next {kpis?.renewal_calendar_days_ahead || 90} days ({kpis?.renewal_calendar.length || 0})
      </h2>
      {!kpis || kpis.renewal_calendar.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 24, textAlign: 'center', color: 'var(--text-hint)', fontSize: 12 }}>
          No subscriptions expire in the next 90 days.
        </div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thStyle}>Contract #</th>
              <th style={thStyle}>Customer</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Monthly</th>
              <th style={thStyle}>Expires</th>
              <th style={thStyle}>Days Left</th>
              <th style={thStyle}>Decision By</th>
              <th style={thStyle}>Auto-Renew</th>
            </tr></thead>
            <tbody>
              {kpis.renewal_calendar.map(r => (
                <tr key={r.contract_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/contracts/${r.contract_id}`)}>
                  <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--accent)' }}>{r.contract_number}</td>
                  <td style={tdStyle}>{r.customer_name || '-'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(r.monthly_amount)}</td>
                  <td style={tdStyle}>{r.end_date}</td>
                  <td style={{ ...tdStyle, color: r.days_to_renewal < 30 ? 'var(--negative)' : r.days_to_renewal < 60 ? 'var(--warning)' : 'var(--text-primary)' }}>{r.days_to_renewal}</td>
                  <td style={tdStyle}>{r.decision_deadline}</td>
                  <td style={tdStyle}>{r.auto_renew ? <span style={{ color: 'var(--positive)', fontSize: 11, fontWeight: 600 }}>YES</span> : <span style={{ color: 'var(--text-hint)', fontSize: 11 }}>NO</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  return (
    <PageLayout title="Revenue Contracts" subtitle="ASC 606 revenue contracts, performance obligations, and SSP allocation">
      <TabBar
        tabs={[
          { key: 'all', label: 'All Contracts', count: contracts.length },
          { key: 'subscriptions', label: 'Subscriptions', count: subscriptions.length },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'all' && allContractsTab}
      {tab === 'subscriptions' && subscriptionsTab}

      {/* Phase 16 Track B — CECL action modal (assign pool / set individual) */}
      <Modal
        open={!!ceclModal && ceclModal.kind !== null}
        onClose={() => { setCeclModal(null); setCeclError(null); }}
        size="md"
        title={
          ceclModal?.kind === 'assign_pool' ? 'Assign CECL Pool'
          : ceclModal?.kind === 'set_individual' ? 'Set Individual Basis'
          : ''
        }
        description={
          ceclModal?.kind === 'assign_pool'
            ? 'Assign the contract to a CECL pool. Allowance will be computed from pool methodology + Q-factor overlay.'
            : 'Per ASC 326-20-30-5, a contract whose risk characteristics diverge from any pool must be evaluated individually with a specific reserve. Rationale >= 60 chars required.'
        }
        footer={
          <>
            <ActionButton
              label="Cancel"
              variant="secondary"
              onClick={() => { setCeclModal(null); setCeclError(null); }}
              disabled={ceclSubmitting}
            />
            <ActionButton
              label={ceclSubmitting ? 'Saving...' : 'Submit'}
              variant="primary"
              disabled={ceclSubmitting}
              onClick={async () => {
                if (!ceclModal) return;
                setCeclSubmitting(true);
                setCeclError(null);
                try {
                  let url: string;
                  let body: any;
                  if (ceclModal.kind === 'assign_pool') {
                    if (!ceclModal.pool_id) {
                      setCeclError('Select a pool first');
                      setCeclSubmitting(false);
                      return;
                    }
                    if (!ceclModal.rationale.trim()) {
                      setCeclError('Rationale required');
                      setCeclSubmitting(false);
                      return;
                    }
                    url = `${REV_API}/contracts/${ceclModal.contract_id}/cecl/assign-pool`;
                    body = { pool_id: ceclModal.pool_id, rationale: ceclModal.rationale };
                  } else {
                    const amt = parseFloat(ceclModal.specific_reserve_amount);
                    if (!isFinite(amt) || amt < 0) {
                      setCeclError('Specific reserve amount must be a non-negative number');
                      setCeclSubmitting(false);
                      return;
                    }
                    if (ceclModal.rationale.length < 60) {
                      setCeclError(`Rationale must be >= 60 chars (currently ${ceclModal.rationale.length})`);
                      setCeclSubmitting(false);
                      return;
                    }
                    url = `${REV_API}/contracts/${ceclModal.contract_id}/cecl/set-individual-basis`;
                    body = {
                      specific_reserve_amount: amt,
                      rationale: ceclModal.rationale,
                    };
                  }
                  const r = await authFetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                  });
                  if (r.ok) {
                    const refresh = await authFetch(
                      `${REV_API}/contracts/${ceclModal.contract_id}/cecl/allowance`,
                    );
                    if (refresh.ok) {
                      const cecl = await refresh.json();
                      setCeclBasis(prev => ({
                        ...prev,
                        [ceclModal.contract_id]: normalizeCECL(cecl, ceclModal.contract_id),
                      }));
                    }
                    setCeclModal(null);
                  } else {
                    const err = await r.json().catch(() => ({}));
                    const detail = err?.detail || err?.message || `HTTP ${r.status}`;
                    const code = err?.error_code || err?.code;
                    if (code === 'CECLEngineDisabledError' || (typeof detail === 'string' && detail.includes('CECLEngineDisabled'))) {
                      setCeclError('Engine inert — enable via CECL Pools page first');
                    } else {
                      setCeclError(typeof detail === 'string' ? detail : JSON.stringify(detail));
                    }
                  }
                } catch (e: any) {
                  setCeclError(e?.message || 'Network error');
                }
                setCeclSubmitting(false);
              }}
            />
          </>
        }
      >
        {ceclError && (
          <div style={{
            padding: '10px 14px', marginBottom: 12, borderRadius: 6,
            background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
            color: 'var(--accent-critical)', fontSize: 12,
          }}>
            {ceclError}
          </div>
        )}

        {ceclModal?.kind === 'assign_pool' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-hint)', letterSpacing: '0.04em' }}>Pool *</label>
              <select
                value={ceclModal.pool_id ?? ''}
                onChange={(e) => setCeclModal(m => m ? { ...m, pool_id: e.target.value ? parseInt(e.target.value, 10) : null } : null)}
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13 }}
              >
                <option value="">— select pool —</option>
                {ceclPoolList.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.pool_code} — {p.pool_name}</option>
                ))}
              </select>
              {ceclPoolList.length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
                  No pools available. Visit CECL Pools page to create one first.
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-hint)', letterSpacing: '0.04em' }}>Rationale *</label>
              <textarea
                rows={3}
                value={ceclModal.rationale}
                onChange={(e) => setCeclModal(m => m ? { ...m, rationale: e.target.value } : null)}
                placeholder="Why this pool is appropriate for the contract's risk characteristics..."
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-sans)' }}
              />
            </div>
          </>
        )}

        {ceclModal?.kind === 'set_individual' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-hint)', letterSpacing: '0.04em' }}>Specific Reserve Amount *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={ceclModal.specific_reserve_amount}
                onChange={(e) => setCeclModal(m => m ? { ...m, specific_reserve_amount: e.target.value } : null)}
                placeholder="0.00"
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13 }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-hint)', letterSpacing: '0.04em' }}>
                Rationale (&gt;= 60 chars, currently {ceclModal.rationale.length}) *
              </label>
              <textarea
                rows={4}
                value={ceclModal.rationale}
                onChange={(e) => setCeclModal(m => m ? { ...m, rationale: e.target.value } : null)}
                placeholder="Cite the risk-characteristic divergence per ASC 326-20-30-5 (e.g. counterparty-specific credit deterioration, unique collateral, single-obligor concentration)..."
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-sans)' }}
              />
              <div style={{ fontSize: 11, color: ceclModal.rationale.length < 60 ? 'var(--accent-critical)' : 'var(--positive)', marginTop: 2 }}>
                {ceclModal.rationale.length < 60
                  ? `${60 - ceclModal.rationale.length} more character(s) required`
                  : 'OK'}
              </div>
            </div>
          </>
        )}
      </Modal>
    </PageLayout>
  );
}

// ── Phase 16 Track B — CECL assessment helper component ───────

interface CECLSectionProps {
  contract: Contract;
  allowance: CECLAllowance | null;
  poolOptions: CECLPoolOption[];
  historyOpen: boolean;
  onToggleHistory: () => void;
  onOpenAssignPool: () => void;
  onOpenSetIndividual: () => void;
  onCompute: () => void;
  error: string | null;
}

function CECLAssessmentSection(props: CECLSectionProps) {
  const { contract, allowance, historyOpen, onToggleHistory, onOpenAssignPool, onOpenSetIndividual, onCompute, error } = props;
  const basis = allowance?.assessment_basis || null;
  const isEngineDisabledError = error && (error.includes('inert') || error.includes('CECLEngineDisabled'));

  const historyCols: Column[] = [
    {
      key: 'fired_at', label: 'When',
      render: (v: string | null) => v ? <span style={{ fontSize: 11 }}>{String(v).replace('T', ' ').slice(0, 19)}</span> : '—',
    },
    { key: 'event_type', label: 'Event', render: (v: string) => <span style={{ fontSize: 11, fontWeight: 600 }}>{v}</span> },
    {
      key: 'amount', label: 'Amount', align: 'right',
      render: (v: number | null) => v != null ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(Number(v))}</span> : '—',
    },
    {
      key: 'direction', label: 'Dir',
      render: (v: string | null) => v ? (
        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--bg-deep)' }}>{v}</span>
      ) : '—',
    },
    { key: 'methodology', label: 'Method', render: (v: string | null) => v ? <span style={{ fontSize: 11 }}>{v}</span> : '—' },
    {
      key: 'je_id', label: 'JE',
      render: (v: number | null) => v ? <span style={{ fontSize: 11, color: 'var(--accent)' }}>#{v}</span> : '—',
    },
    { key: 'triggered_by', label: 'Trigger', render: (v: string | null) => v ? <span style={{ fontSize: 11 }}>{v}</span> : '—' },
  ];

  return (
    <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-900)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          CECL Assessment · Contract #{contract.id}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <ActionButton label="Assign Pool" size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); onOpenAssignPool(); }} />
          <ActionButton label="Set Individual Basis" size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); onOpenSetIndividual(); }} />
          <ActionButton
            label="Compute Allowance"
            size="sm"
            variant="primary"
            disabled={!basis}
            onClick={(e) => { e.stopPropagation(); onCompute(); }}
          />
          <ActionButton
            label={historyOpen ? 'Hide History' : 'History'}
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); onToggleHistory(); }}
          />
        </div>
      </div>

      {isEngineDisabledError && (
        <div style={{
          padding: '8px 12px', marginBottom: 10, borderRadius: 6,
          background: 'rgba(245,158,11,0.10)',
          border: '1px solid rgba(245,158,11,0.30)',
          fontSize: 12, color: 'var(--text-primary)',
        }}>
          Engine inert — enable via CECL Pools page first
        </div>
      )}

      {error && !isEngineDisabledError && (
        <div style={{
          padding: '8px 12px', marginBottom: 10, borderRadius: 6,
          background: 'rgba(239,68,68,0.10)',
          border: '1px solid rgba(239,68,68,0.30)',
          fontSize: 12, color: 'var(--accent-critical)',
        }}>
          {error}
        </div>
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 10, marginBottom: 8,
      }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-hint)', fontWeight: 600, textTransform: 'uppercase' }}>Basis</div>
          <div style={{
            fontSize: 14, fontWeight: 700,
            color: basis === 'POOL' ? 'var(--accent)'
              : basis === 'INDIVIDUAL' ? 'var(--warning)'
              : 'var(--text-hint)',
          }}>{basis || 'not set'}</div>
        </div>
        {basis === 'POOL' && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-hint)', fontWeight: 600, textTransform: 'uppercase' }}>Pool</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {allowance?.pool_code ? `${allowance.pool_code}` : `#${allowance?.pool_id ?? '—'}`}
              {allowance?.pool_name ? <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-secondary)' }}> — {allowance.pool_name}</span> : null}
            </div>
          </div>
        )}
        {basis === 'INDIVIDUAL' && allowance?.specific_reserve_amount != null && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-hint)', fontWeight: 600, textTransform: 'uppercase' }}>Specific Reserve</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{formatAmount(Number(allowance.specific_reserve_amount))}</div>
          </div>
        )}
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-hint)', fontWeight: 600, textTransform: 'uppercase' }}>Current Allowance</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-critical)' }}>
            {allowance?.current_allowance_amount != null ? formatAmount(Number(allowance.current_allowance_amount)) : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-hint)', fontWeight: 600, textTransform: 'uppercase' }}>Current Exposure</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {allowance?.current_exposure != null ? formatAmount(Number(allowance.current_exposure)) : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-hint)', fontWeight: 600, textTransform: 'uppercase' }}>Last Computed</div>
          <div style={{ fontSize: 12 }}>
            {allowance?.last_computed_at
              ? String(allowance.last_computed_at).replace('T', ' ').slice(0, 19)
              : 'never'}
          </div>
        </div>
      </div>

      {basis === 'INDIVIDUAL' && allowance?.rationale && (
        <div style={{ padding: '8px 10px', background: 'var(--bg-deep)', borderRadius: 4, fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5 }}>
          <strong>Rationale:</strong> {allowance.rationale}
        </div>
      )}

      {historyOpen && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', marginBottom: 6 }}>
            Allowance History ({allowance?.history?.length || 0})
          </div>
          <DataTable
            columns={historyCols}
            rows={allowance?.history || []}
            emptyMessage="No allowance history yet. Run Compute Allowance to record the first event."
          />
        </div>
      )}
    </div>
  );
}
